import { useState, useEffect, useContext } from "react";
import Swal from "sweetalert2";
import {
    Backdrop,
    Paper,
    Stack,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
} from "@mui/material";

import { UserContext } from "../../context/UserContext";

import { handleNumberInput } from "../utils";

import CustomModalHeader from "../custom_components/CustomModalHeader";
import CustomButton from "../custom_components/CustomButton";

export default function Edit_Payment({
    open,
    onClose,
    onUpdate,
    selectedPayment,
    orderInfo,
    totalPaidAmount,
}) {

    const apiUrl = process.env.REACT_APP_API_URL;

    const { paymentMethods } = useContext(UserContext);

    const [paidAmount, setPaidAmount] = useState("");
    const [paymentMethod, setPaymentMethod] = useState("");
    const [paymentDate, setPaymentDate] = useState("");
    const [paymentRemarks, setPaymentRemarks] = useState("");

    const [isSaving, setIsSaving] = useState(false);

    /* OPEN + PREFILL */
    useEffect(() => {
        if (selectedPayment) {
            setPaidAmount(selectedPayment.PaidAmount || "");
            setPaymentMethod(selectedPayment.PaymentMethod || "");
            setPaymentDate(selectedPayment?.PaymentDate || "");
            setPaymentRemarks(selectedPayment.PaymentRemarks || "");
        }
    }, [selectedPayment, open]);

    const handleClose = () => {
        if (isSaving) return;
        onClose();
    };

    /* UPDATE PAYMENT */
    const handleUpdatePaymentHistory = async () => {
        if (!paidAmount || !paymentMethod || !paymentDate) {
            Swal.fire({
                text: "Please complete all required inputs.",
                icon: "warning",
            });
            return;
        }

        const itemAmount =
            (Number(orderInfo.Price || 0) * Number(orderInfo.ItemQty || 0)) +
            Number(orderInfo.SFee || 0);

        const paymentAmount = Number(paidAmount || 0);
        const oldPaymentAmount = Number(selectedPayment.PaidAmount || 0);
        const adjustedTotalPaid = totalPaidAmount - oldPaymentAmount;
        const remainingBalance = itemAmount - adjustedTotalPaid;

        if (paymentAmount <= 0) {

            Swal.fire({
                icon: "warning",
                title: "Invalid Payment",
                text: "Payment amount must be greater than ₱0."
            });

            return;

        } else if (paymentAmount > remainingBalance) {

            Swal.fire({
                icon: "warning",
                title: "Overpayment",
                text: `The maximum payment allowed is ₱${remainingBalance.toLocaleString()}`
            });
            return;

        }

        setIsSaving(true);

        try {
            const response = await fetch(`${apiUrl}manage_payment/update_payment_history.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    custID: orderInfo.CustomerID,
                    orderID: orderInfo.OrderID,
                    paymentID: selectedPayment.id,
                    paidAmount: paidAmount,
                    payMethod: paymentMethod,
                    payDate: paymentDate,
                    payRemarks: paymentRemarks,
                }),
            });

            const result = await response.json();

            if (result.status === "success") {
                Swal.fire({
                    icon: "success",
                    text: "Payment updated successfully",
                    timer: 1500,
                    showConfirmButton: false,
                });

                onUpdate({
                    id: selectedPayment.id,
                    CustomerID: orderInfo.CustomerID,
                    OrderID: orderInfo.OrderID,
                    PaidAmount: paidAmount,
                    PaymentDate: paymentDate,
                    PaymentMethod: paymentMethod,
                    PaymentRemarks: paymentRemarks,
                });

                handleClose();

            } else {
                Swal.fire({
                    text: result.message,
                    icon: "error"
                });
            }

        } catch (error) {
            Swal.fire({
                title: "Error updating payment!",
                text: error.toString(),
                icon: "error",
            });
            console.error(error);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Backdrop
            aria-hidden="false"
            open={open}
            sx={{
                zIndex: (theme) => theme.zIndex.drawer + 1,
                backdropFilter: "blur(1px)",
            }}
        >
            <Paper
                sx={{
                    width: 400,
                    p: 3,
                    borderRadius: 2,

                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "4px",
                        background: "#2e3489"
                    }
                }}
            >

                <CustomModalHeader
                    title="Edit Payment"
                    subtitle="Edit payment information"
                    onClose={onClose}
                />

                <Stack spacing={2}>
                    <TextField
                        label="Paid Amount"
                        size="small"
                        autoComplete="off"
                        value={paidAmount}
                        onChange={(e) => handleNumberInput(e, setPaidAmount)}
                        fullWidth
                    />

                    <FormControl size="small" fullWidth>
                        <InputLabel>Payment Method</InputLabel>
                        <Select
                            value={paymentMethod ?? selectedPayment?.PaymentMethodID ?? ""}
                            label="Payment Method"
                            onChange={(e) => setPaymentMethod(e.target.value)}
                        >
                            {paymentMethods.map((pm) => (
                                <MenuItem
                                    key={pm.id}
                                    value={pm.id}
                                    sx={{ py: 1.25 }}
                                >
                                    <Stack spacing={0.5} width="100%">
                                        <Typography
                                            fontSize={12}
                                            fontWeight={600}
                                        >
                                            {pm.AccType}
                                        </Typography>

                                        <Stack
                                            direction="row"
                                            justifyContent="space-between"
                                            alignItems="center"
                                        >
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                {`${pm.AccName} • ${pm.AccNumber}`}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        label="Payment Date"
                        type="date"
                        size="small"
                        value={paymentDate}
                        onChange={(e) => setPaymentDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                    />

                    <TextField
                        label="Remarks"
                        size="small"
                        autoComplete="off"
                        value={paymentRemarks}
                        onChange={(e) => setPaymentRemarks(e.target.value)}
                        fullWidth
                        multiline
                        rows={3}
                    />

                    {/* ACTION BUTTONS */}
                    <Stack direction="row" spacing={2} mt={3} justifyContent="flex-end">
                        <CustomButton
                            label="Cancel"
                            variant="outlined"
                            hoverColor="#d14249"
                            onClick={onClose}
                        />
                        <CustomButton
                            label={isSaving ? "Saving..." : "Save Changes"}
                            disabled={isSaving}
                            onClick={handleUpdatePaymentHistory}
                        />
                    </Stack>
                </Stack>

            </Paper>

        </Backdrop>
    );
}
